import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, CheckCircle2, Star } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { StarInput } from "@/components/StarRating";

const input = "w-full rounded-sm bg-input border border-gold/20 px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-gold transition-shadow duration-200";
const label = "block text-xs uppercase tracking-wider text-slate-400 mb-1.5";

export default function ReviewSubmit() {
  const { bookingId } = useParams();
  const [rating, setRating] = useState(0);
  const [name, setName] = useState("");
  const [comment, setComment] = useState("");
  const [serviceType, setServiceType] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [alreadyReviewed, setAlreadyReviewed] = useState(false);

  useEffect(() => {
    if (!bookingId) return;
    api.get(`/review-context/${bookingId}`)
      .then(({ data }) => {
        setName(data.name || "");
        setServiceType(data.service_type || "");
        if (data.already_reviewed) setAlreadyReviewed(true);
      })
      .catch(() => {});
  }, [bookingId]);

  const submit = async () => {
    if (rating < 1) { toast.error("Please tap the stars to rate your experience."); return; }
    setLoading(true);
    try {
      await api.post("/reviews", {
        name: name.trim() || undefined,
        rating,
        comment: comment.trim() || undefined,
        service_type: serviceType || undefined,
        booking_id: bookingId || undefined,
      });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit your review.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] max-w-2xl mx-auto px-5 sm:px-10 pt-14 sm:pt-20 pb-24">
      <span className="text-sm uppercase tracking-[0.2em] text-gold">Your feedback</span>
      <h1 className="mt-3 font-heading text-4xl sm:text-5xl font-bold text-white tracking-tighter">Rate your trip</h1>
      <p className="mt-4 text-lg text-slate-300">Thank you for riding with Bridge Taxi. Your honest review helps us improve and helps other travellers.</p>

      {done ? (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-10 rounded-sm border border-gold/25 bg-navy p-8 text-center" data-testid="review-done">
          <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-400" />
          <h2 className="mt-4 font-heading text-2xl font-bold text-white">Thank you for your review!</h2>
          <p className="mt-2 text-slate-300">Your review has been submitted and will appear on our site once approved. Thank you!</p>
          <a href="/reviews" data-testid="review-see-all-btn"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-semibold text-navy hover:bg-gold-light transition-colors duration-200">
            <Star className="h-4 w-4 fill-navy" /> See our reviews
          </a>
        </motion.div>
      ) : alreadyReviewed ? (
        <div className="mt-10 rounded-sm border border-gold/25 bg-navy p-8 text-center" data-testid="review-already">
          <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-400" />
          <h2 className="mt-4 font-heading text-2xl font-bold text-white">You've already reviewed this trip</h2>
          <p className="mt-2 text-slate-300">Thanks so much — we appreciate your feedback!</p>
          <a href="/reviews" className="mt-6 inline-flex items-center gap-2 rounded-full border border-gold/40 px-6 py-3 text-sm font-semibold text-white hover:border-gold hover:text-gold transition-colors duration-200">
            <Star className="h-4 w-4 text-gold fill-gold" /> See our reviews
          </a>
        </div>
      ) : (
        <div className="mt-10 space-y-6 rounded-sm border border-gold/20 bg-navy-light p-6 sm:p-8">
          <div>
            <label className={label}>How would you rate us?</label>
            <StarInput value={rating} onChange={setRating} />
          </div>
          <div>
            <label className={label}>Your name <span className="text-slate-500">(optional)</span></label>
            <input data-testid="review-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Sarah M." className={input} />
          </div>
          <div>
            <label className={label}>Tell us about your experience <span className="text-slate-500">(optional)</span></label>
            <textarea data-testid="review-comment" value={comment} onChange={(e) => setComment(e.target.value)} rows={5}
              placeholder="What did you love? Was the driver on time and friendly?" className={`${input} resize-none`} />
          </div>
          <button onClick={submit} disabled={loading} data-testid="review-submit-btn"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold py-3.5 font-semibold text-navy hover:bg-gold-light transition-colors duration-200 disabled:opacity-60">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Star className="h-4 w-4 fill-navy" />} Submit review
          </button>
          <p className="text-center text-xs text-slate-500">Reviews are checked before appearing publicly.</p>
        </div>
      )}
    </div>
  );
}
